import React from 'react';
import { 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  ListItemIcon,
  Divider,
  Stack,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { 
  Home as HomeIcon, 
  ViewModule as ComponentsIcon,
  Palette as PaletteIcon,
  TextFields as TypographyIcon,
  GridOn as SpacingIcon,
  AccountBalance as FinancialIcon,
  Timeline as ProcessIcon,
  OpenInNew as ModalsIcon,
  Dashboard as DashboardIcon,
  PersonOutlined,
  LogoutOutlined,
  SettingsOutlined,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import DarkModeToggle from '../components/DarkModeToggle/DarkModeToggle';

const drawerWidth = 280;

const ResponsiveLayout = ({ children }) => {
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  
  // User menu state
  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);
  const userMenuOpen = Boolean(userMenuAnchor);
  
  // Mobile drawer state
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleSignOut = () => {
    signOut();
    handleUserMenuClose();
  };

  const menuSections = [
    {
      title: 'Getting Started',
      items: [
        { text: 'Overview', path: '/', icon: <HomeIcon /> },
        { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
      ]
    },
    {
      title: 'Foundation',
      items: [
        { text: 'Colors', path: '/colors', icon: <PaletteIcon /> },
        { text: 'Typography', path: '/typography', icon: <TypographyIcon /> },
        { text: 'Spacing', path: '/spacing', icon: <SpacingIcon /> },
      ]
    },
    {
      title: 'Components',
      items: [
        { text: 'Buttons', path: '/buttons', icon: <ComponentsIcon /> },
        { text: 'Inputs & Forms', path: '/inputs', icon: <ComponentsIcon /> },
        { text: 'Data Display', path: '/data-display', icon: <ComponentsIcon /> },
        { text: 'Feedback', path: '/feedback', icon: <ComponentsIcon /> },
        { text: 'Layout', path: '/layout', icon: <ComponentsIcon /> },
        { text: 'Navigation', path: '/navigation', icon: <ComponentsIcon /> },
        { text: 'Modals', path: '/modals', icon: <ModalsIcon /> },
      ]
    },
    {
      title: 'Advanced Components',
      items: [
        { text: 'Financial', path: '/financial', icon: <FinancialIcon /> },
        { text: 'Process Flow', path: '/process-flow', icon: <ProcessIcon /> },
      ]
    }
  ];

  // Drawer Content Component
  const DrawerContent = ({ onItemClick = () => {} }) => (
    <Box>
      {/* Drawer Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: 64,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6" color="white" fontWeight={700}>
              A
            </Typography>
          </Box>
          <Typography variant="h6" color="gray.g700" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>
            Autobia DS
          </Typography>
        </Stack>
        {isMobile && (
          <IconButton onClick={handleMobileDrawerClose} sx={{ ml: 'auto' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      
      <Divider />
      
      {/* Navigation Menu */}
      <Box sx={{ overflow: 'auto', p: 2 }}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={section.title} sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'gray.g500',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                px: 2,
                py: 1,
                display: 'block',
              }}
            >
              {section.title}
            </Typography>
            <List dense disablePadding>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <ListItem
                    key={item.text}
                    component={Link}
                    to={item.path}
                    onClick={onItemClick}
                    sx={{
                      borderRadius: '8px',
                      mb: 0.5,
                      backgroundColor: isActive ? 'primary.main' : 'transparent',
                      color: isActive ? 'white' : 'gray.g700',
                      '&:hover': {
                        backgroundColor: isActive ? 'primary.dark' : 'gray.g100',
                      },
                      '& .MuiListItemIcon-root': {
                        color: isActive ? 'white' : 'gray.g500',
                        minWidth: 40,
                      },
                      textDecoration: 'none',
                    }}
                  >
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: isActive ? 600 : 500,
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
            {sectionIndex < menuSections.length - 1 && (
              <Divider sx={{ my: 2, backgroundColor: 'gray.g200' }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile App Bar */}
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          display: { lg: 'none' },
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'gray.g200',
          boxShadow: 'none',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          {/* Mobile Menu Button */}
          <IconButton
            edge="start"
            onClick={handleMobileDrawerToggle}
            sx={{ mr: { xs: 0.5, sm: 1 }, p: { xs: 0.5, sm: 1 }, color: 'gray.g700' }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Mobile Title */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
            <Box
              sx={{
                width: { xs: 24, sm: 32 },
                height: { xs: 24, sm: 32 },
                borderRadius: '6px',
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" color="white" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                A
              </Typography>
            </Box>
            <Typography variant="h6" color="gray.g700" fontWeight={600} sx={{ display: { xs: 'none', md: 'block' } }}>
              Autobia Design System
            </Typography>
          </Stack>

          {/* Mobile User Menu */}
          <Stack direction="row" alignItems="center" spacing={0}>
            <DarkModeToggle size="small" />
            <Chip 
              label="Authorized" 
              color="success" 
              variant="outlined" 
              size="small"
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            />
            <IconButton
              onClick={handleUserMenuClick}
              size="small"
              aria-controls={userMenuOpen ? 'user-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={userMenuOpen ? 'true' : undefined}
              sx={{ p: { xs: 0.25, sm: 0.5 } }}
            >
              <Avatar 
                src={user?.imageUrl}
                sx={{ width: { xs: 24, sm: 32, md: 36 }, height: { xs: 24, sm: 32, md: 36 } }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Desktop App Bar */}
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'gray.g200',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
            <Typography variant="h6" color="gray.g700" fontWeight={600}>
              Autobia Design System
            </Typography>
          </Stack>

          {/* Desktop User Menu */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <DarkModeToggle />
            <Chip 
              label="Authorized" 
              color="success" 
              variant="outlined" 
              size="small"
            />
            <IconButton
              onClick={handleUserMenuClick}
              size="small"
              aria-controls={userMenuOpen ? 'user-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={userMenuOpen ? 'true' : undefined}
            >
              <Avatar 
                src={user?.imageUrl}
                sx={{ width: 36, height: 36 }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        id="user-menu"
        open={userMenuOpen}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              minWidth: 200,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.fullName || 'Autobia Employee'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.primaryEmailAddress?.emailAddress}
          </Typography>
        </Box>
        <MenuItem onClick={handleUserMenuClose} component={Link} to="/dashboard">
          <PersonOutlined fontSize="small" sx={{ mr: 1 }} />
          My Profile
        </MenuItem>
        <MenuItem onClick={handleUserMenuClose}>
          <SettingsOutlined fontSize="small" sx={{ mr: 1 }} />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSignOut} sx={{ color: 'error.main' }}>
          <LogoutOutlined fontSize="small" sx={{ mr: 1 }} />
          Sign Out
        </MenuItem>
      </Menu>
      
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'gray.g25',
            borderRight: '1px solid',
            borderColor: 'gray.g200',
          },
        }}
      >
        <DrawerContent onItemClick={handleMobileDrawerClose} />
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'gray.g25',
            borderRight: '1px solid',
            borderColor: 'gray.g200',
          },
        }}
      >
        <DrawerContent />
      </Drawer>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          minHeight: '100vh',
          width: { xs: '100%', lg: `calc(100% - ${drawerWidth}px)` },
          ml: { lg: 0 }, // Don't add margin on desktop, drawer is permanent
        }}
      >
        {/* Toolbar spacer */}
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }} />
        
        {/* Content with responsive padding */}
        <Container 
          maxWidth={false}
          sx={{ 
            py: { xs: 2, sm: 3, md: 4 },
            px: { xs: 1, sm: 2, md: 3, lg: 4 },
            maxWidth: { xs: '100%', xl: '1536px' },
            width: '100%'
          }}
        >
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default ResponsiveLayout;